import { useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { supabase } from './lib/supabase';
import LoginPage from './pages/LoginPage';
import WorkerApp from './pages/WorkerApp';
import ManagerApp from './pages/ManagerApp';
import TownPortalApp from './pages/TownPortalApp';

// Shown only when started via `npm run start:demo`. On a shared screen there is
// otherwise nothing to tell the demo apart from the live system, and the two
// look identical because they are the same app.
function DemoBadge() {
  if (process.env.REACT_APP_DEMO_MODE !== 'true') return null;
  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      zIndex: 1000,
      background: '#7c2d12',
      color: '#fed7aa',
      textAlign: 'center',
      fontSize: 11,
      fontWeight: 800,
      letterSpacing: 1.5,
      padding: '3px 0',
      pointerEvents: 'none',
    }}>
      DEMO — NOT LIVE DATA
    </div>
  );
}

function RoleSwitcher({ currentMode, onSwitch, role }) {
  const isWorkerMode = currentMode === 'worker';
  return (
    <div style={{
      position: 'fixed',
      bottom: 16,
      right: 16,
      zIndex: 999,
    }}>
      <button
        onClick={onSwitch}
        style={{
          background: isWorkerMode ? '#1e293b' : '#f97316',
          color: isWorkerMode ? '#f97316' : '#000',
          border: isWorkerMode ? '2px solid #f97316' : '2px solid #f97316',
          borderRadius: 14,
          padding: '10px 18px',
          fontSize: 13,
          fontWeight: 800,
          cursor: 'pointer',
          boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
        }}
      >
        {isWorkerMode ? (
          <>{role === 'manager' ? '◉ Manager View' : '◉ Supervisor View'}</>
        ) : (
          <>☑ Worker View</>
        )}
      </button>
    </div>
  );
}

function AppRoutes() {
  const { user, profile, loading, recovering } = useAuth();
  const [mode, setMode] = useState('default');

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: '#0f172a',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        gap: 16,
      }}>
        <div style={{ color: '#f97316', fontSize: 28, fontWeight: 700, letterSpacing: -1 }}>
          TraverseClean
        </div>
        <div style={{ color: '#64748b', fontSize: 14 }}>Loading...</div>
      </div>
    );
  }

  if (recovering || !user) return <LoginPage />;

  // Managers and supervisors can toggle between dashboard and worker mode
  if (profile?.role === 'manager' || profile?.role === 'supervisor') {
    const isWorkerMode = mode === 'worker';

    return (
      <>
        {isWorkerMode ? (
          <Routes>
            <Route path="/worker/*" element={<WorkerApp />} />
            <Route path="*" element={<Navigate to="/worker" replace />} />
          </Routes>
        ) : (
          <Routes>
            <Route path="/manager/*" element={<ManagerApp />} />
            <Route path="*" element={<Navigate to="/manager" replace />} />
          </Routes>
        )}
        <RoleSwitcher
          currentMode={isWorkerMode ? 'worker' : 'manager'}
          onSwitch={() => setMode(isWorkerMode ? 'default' : 'worker')}
          role={profile.role}
        />
      </>
    );
  }

  if (profile?.role === 'worker') {
    return (
      <Routes>
        <Route path="/worker/*" element={<WorkerApp />} />
        <Route path="*" element={<Navigate to="/worker" replace />} />
      </Routes>
    );
  }

  if (profile?.role === 'town') {
    return (
      <Routes>
        <Route path="/town/*" element={<TownPortalApp key={profile?.id} profile={profile} onLogout={() => supabase.auth.signOut()} />} />
        <Route path="*" element={<Navigate to="/town" replace />} />
      </Routes>
    );
  }

  // Profile not set up yet
  return <LoginPage />;
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <DemoBadge />
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}